import { useState } from "react";
import { useData } from "../../context/DataContext";
import { Card, CardHead, Btn, TableWrap, Th, Td, Pill, EmptyState, IconBtn } from "../kit";
import { fmtINR, uid, todayStr, pipelineAdvanceTotal, pipelineWhatsApp } from "../../lib/finance";
import { toast } from "sonner";
import { CheckCircle2, Trash2, Copy, IndianRupee } from "lucide-react";

const inp = "rounded-lg border border-slate-200 px-2 py-1 text-sm";

export default function Pipeline() {
  const { pipeline, savePipeline, entries, saveEntries } = useData();
  const rows = pipeline.slice().sort((a, b) => (a.expectedDate || "").localeCompare(b.expectedDate || ""));
  const [editId, setEditId] = useState(null);
  const [ev, setEv] = useState({});
  const [advId, setAdvId] = useState(null);
  const [adv, setAdv] = useState({ amount: "", date: todayStr() });

  const startEdit = (p) => { setAdvId(null); setEditId(p.id); setEv({ ...p, advanceTotal: pipelineAdvanceTotal(p) }); };
  const saveEdit = async () => {
    const orig = pipeline.find((p) => p.id === editId);
    if (!orig) return setEditId(null);
    const newAdv = parseFloat(ev.advanceTotal) || 0;
    if (newAdv < 0) return toast.error("Advance can't be negative");
    const oldAdv = pipelineAdvanceTotal(orig);
    const brand = (ev.brand || "").trim() || orig.brand;
    let advances = orig.advances || [];

    if (newAdv !== oldAdv || (brand !== orig.brand && advances.length)) {
      // Keep the ledger in sync: replace this deal's advance lines with one line for the new total.
      const ids = new Set(advances.map((a) => a.entryId).filter(Boolean));
      const isAdvLine = (e) => ids.has(e.id) || (e.pipelineId === orig.id && /— advance \(pipeline\)$/.test(e.description || ""));
      const date = advances.length ? advances.map((a) => a.date).sort().slice(-1)[0] : todayStr();
      let nextEntries = entries.filter((e) => !isAdvLine(e));
      advances = [];
      if (newAdv > 0) {
        const entryId = uid();
        nextEntries = [...nextEntries, { id: entryId, type: "income", category: "Brand Collabs", amount: newAdv, description: `${brand} — advance (pipeline)`, date, pipelineId: orig.id }];
        advances = [{ id: uid(), amount: newAdv, date, entryId }];
      }
      await saveEntries(nextEntries);
    }

    await savePipeline(pipeline.map((p) => (p.id === editId ? { ...p, brand, expectedAmount: parseFloat(ev.expectedAmount) || 0, expectedDate: ev.expectedDate, status: ev.status, notes: ev.notes, advances } : p)));
    setEditId(null);
    toast.success(newAdv !== oldAdv ? `Pipeline entry updated — ledger advance set to ${fmtINR(newAdv)}` : "Pipeline entry updated");
  };

  const del = async (p) => {
    const paid = pipelineAdvanceTotal(p);
    const msg = paid > 0
      ? `Remove ${p.brand} from the pipeline?\n\nThe ${fmtINR(paid)} advance already received stays in the ledger.`
      : "Remove this from the pipeline?";
    if (!window.confirm(msg)) return;
    await savePipeline(pipeline.filter((x) => x.id !== p.id));
    toast.success("Removed from pipeline");
  };

  // Advance payment: written to the ledger immediately and remembered on the pipeline deal.
  const startAdvance = (p) => { setEditId(null); setAdvId(p.id); setAdv({ amount: "", date: todayStr() }); };
  const saveAdvance = async (p) => {
    const amount = parseFloat(adv.amount);
    if (!amount || amount <= 0) return toast.error("Enter the advance amount");
    if (!adv.date) return toast.error("Enter the date received");
    const entryId = uid();
    await saveEntries([...entries, { id: entryId, type: "income", category: "Brand Collabs", amount, description: `${p.brand} — advance (pipeline)`, date: adv.date, pipelineId: p.id }]);
    await savePipeline(pipeline.map((x) => (x.id === p.id ? { ...x, advances: [...(x.advances || []), { id: uid(), amount, date: adv.date, entryId }] } : x)));
    setAdvId(null);
    toast.success(`${fmtINR(amount)} advance from ${p.brand} added to the ledger`);
  };

  // Confirm: only the remaining balance (expected minus advances) is written to the ledger.
  const finalize = async (p) => {
    const paid = pipelineAdvanceTotal(p);
    const remaining = Math.max((p.expectedAmount || 0) - paid, 0);
    const label = paid > 0
      ? `Remaining balance received from ${p.brand}?\n(Expected ${fmtINR(p.expectedAmount || 0)} − advance ${fmtINR(paid)})\nEnter 0 if nothing more is due.`
      : `Confirmed amount for ${p.brand}?`;
    const amountStr = window.prompt(label, remaining);
    if (amountStr === null) return;
    const amount = parseFloat(amountStr);
    if (isNaN(amount) || amount < 0 || (amount === 0 && paid === 0)) return toast.error("Enter a valid amount");
    let nextEntries = entries;
    if (amount > 0) {
      const dateStr = window.prompt("Date received?", p.expectedDate || todayStr());
      if (dateStr === null) return;
      nextEntries = [...entries, { id: uid(), type: "income", category: "Brand Collabs", amount, description: paid > 0 ? `${p.brand} — balance (from pipeline)` : `${p.brand} (from pipeline)`, date: dateStr, pipelineId: p.id }];
      await saveEntries(nextEntries);
    }
    await savePipeline(pipeline.filter((x) => x.id !== p.id));
    toast.success(amount > 0 ? `${p.brand} confirmed — ${fmtINR(amount)} added to income` : `${p.brand} confirmed — closed with the advance already received`);
  };

  const copy = () => {
    navigator.clipboard.writeText(pipelineWhatsApp(pipeline))
      .then(() => toast.success("Copied — paste straight into WhatsApp"))
      .catch(() => toast.error("Could not copy"));
  };

  return (
    <Card>
      <CardHead
        title="Brand Collab Pipeline"
        subtitle="Upcoming / unconfirmed collabs — only advances received are counted as income until confirmed"
        right={<Btn variant="outline" onClick={copy} data-testid="pipeline-whatsapp-copy-button"><Copy className="h-4 w-4" /> WhatsApp summary</Btn>}
      />
      <div className="mt-4">
        {rows.length === 0 ? <EmptyState>Nothing in the pipeline yet. Add one from "Add Entry" → Brand Collab → Upcoming.</EmptyState> : (
          <TableWrap>
            <thead><tr><Th>Brand</Th><Th className="text-right">Expected ₹</Th><Th className="text-right">Advance</Th><Th className="text-right">Balance</Th><Th>Date</Th><Th>Stage</Th><Th>Notes</Th><Th></Th></tr></thead>
            <tbody>
              {rows.map((p) => {
                const paid = pipelineAdvanceTotal(p);
                const balance = Math.max((p.expectedAmount || 0) - paid, 0);
                if (editId === p.id) return (
                  <tr key={p.id} className="bg-slate-50">
                    <Td><input className={`w-full ${inp}`} value={ev.brand || ""} onChange={(e) => setEv({ ...ev, brand: e.target.value })} /></Td>
                    <Td><input type="number" className={`w-24 text-right ${inp}`} value={ev.expectedAmount || 0} onChange={(e) => setEv({ ...ev, expectedAmount: e.target.value })} /></Td>
                    <Td><input type="number" min="0" placeholder="0" title="Total advance received — the ledger is updated to match when you save" className={`w-24 text-right ${inp}`} value={ev.advanceTotal || ""} onChange={(e) => setEv({ ...ev, advanceTotal: e.target.value })} /></Td>
                    <Td className="text-right tnum text-slate-500">{fmtINR(Math.max((parseFloat(ev.expectedAmount) || 0) - (parseFloat(ev.advanceTotal) || 0), 0))}</Td>
                    <Td><input type="date" className={inp} value={ev.expectedDate || ""} onChange={(e) => setEv({ ...ev, expectedDate: e.target.value })} /></Td>
                    <Td>
                      <select className={inp} value={ev.status} onChange={(e) => setEv({ ...ev, status: e.target.value })}>
                        <option>Pipeline</option><option>In Talks</option><option>Verbally Confirmed</option>
                      </select>
                    </Td>
                    <Td><input className={`w-full ${inp}`} value={ev.notes || ""} onChange={(e) => setEv({ ...ev, notes: e.target.value })} /></Td>
                    <Td><div className="flex gap-1"><IconBtn onClick={saveEdit} className="text-emerald-600">✓</IconBtn><IconBtn onClick={() => setEditId(null)}>✕</IconBtn></div></Td>
                  </tr>
                );
                return [
                  <tr key={p.id} className="hover:bg-slate-50">
                    <Td className="font-medium text-slate-900">{p.brand}</Td>
                    <Td className="text-right tnum text-emerald-600 font-semibold">{fmtINR(p.expectedAmount || 0)}</Td>
                    <Td className="text-right tnum text-slate-700">{paid ? fmtINR(paid) : "—"}</Td>
                    <Td className="text-right tnum">{paid ? (balance > 0 ? <Pill tone="brand">{fmtINR(balance)}</Pill> : <Pill tone="income">Paid</Pill>) : <span className="text-slate-500">{fmtINR(balance)}</span>}</Td>
                    <Td className="text-slate-500">{p.expectedDate || "—"}</Td>
                    <Td><Pill tone="brand">{p.status}</Pill></Td>
                    <Td className="text-slate-500">{p.notes}</Td>
                    <Td>
                      <div className="flex gap-1">
                        <IconBtn title="Add advance payment" onClick={() => startAdvance(p)} className="text-amber-600" data-testid={`advance-${p.id}`}><IndianRupee className="h-4 w-4" /></IconBtn>
                        <IconBtn title={paid ? "Mark confirmed (logs the remaining balance)" : "Mark confirmed"} onClick={() => finalize(p)} className="text-emerald-600" data-testid={`finalize-${p.id}`}><CheckCircle2 className="h-4 w-4" /></IconBtn>
                        <IconBtn title="Edit" onClick={() => startEdit(p)}>✎</IconBtn>
                        <IconBtn title="Delete" onClick={() => del(p)} className="hover:text-rose-600"><Trash2 className="h-4 w-4" /></IconBtn>
                      </div>
                    </Td>
                  </tr>,
                  advId === p.id && (
                    <tr key={`${p.id}-adv`} className="bg-amber-50/60">
                      <Td colSpan={8}>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="font-medium text-slate-700">Advance from {p.brand}:</span>
                          <input type="number" autoFocus placeholder="Amount ₹" className={`w-32 text-right ${inp}`} value={adv.amount} onChange={(e) => setAdv({ ...adv, amount: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveAdvance(p)} />
                          <input type="date" className={inp} value={adv.date} onChange={(e) => setAdv({ ...adv, date: e.target.value })} />
                          <Btn onClick={() => saveAdvance(p)}>Add to ledger</Btn>
                          <Btn variant="outline" onClick={() => setAdvId(null)}>Cancel</Btn>
                          {(p.advances || []).length > 0 && (
                            <span className="text-slate-500">Already received: {p.advances.map((a) => `${fmtINR(a.amount)} on ${a.date}`).join(", ")}</span>
                          )}
                        </div>
                      </Td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </TableWrap>
        )}
      </div>
      <div className="h-4" />
    </Card>
  );
}
